// Marketplace: gestão de pedidos fabricados por marcenarias parceiras.
//
// Divide os pedidos por parceiro (Piemonte/Domoby/Cobalto) baseado na cidade,
// mostra o quanto MINI Deck deve pagar (78% do valor total, móveis + frete).
//
// Fluxo de pagamento em remessa:
//   1. Vendedora/gerente seleciona pedidos ENTREGUES ainda não pagos (checkbox)
//   2. Barra flutuante mostra "N pedidos • R$ X • [Fechar remessa]"
//   3. Ao fechar, cria uma Remessa (data, parceiro, pedidoIds, valorTotal)
//      e os pedidos ficam com pagamento registrado (referenciando a remessa)
//   4. Aba "Remessas" mostra histórico com PDF individual + botão desfazer
//
// Pedidos de JP não entram — Arco Marcenaria é interna.

import { useState, useMemo, useEffect } from 'react'
import { v4 as uuid } from 'uuid'
import { useOrders } from '../hooks/useOrders'
import { PARCEIROS, parceiroDoPedido, calcularFinanceiro, statusMarketplace, PARCEIRO_PCT } from '../data/marketplace'
import {
  getMarketplacePagamentos, setMarketplacePago,
  getMarketplaceRemessas, saveMarketplaceRemessa, deleteMarketplaceRemessa,
} from '../utils/storage'
import { fmtMoeda } from '../utils/fmt'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { FileDown, CheckCircle2, Clock, TrendingUp, Package, Send, X, Trash2 } from 'lucide-react'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']

function fmtData(iso) {
  if (!iso) return '—'
  try { return format(new Date(iso + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR }) } catch { return iso }
}

function fmtDataHora(iso) {
  if (!iso) return '—'
  try { return format(new Date(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) } catch { return iso }
}

export function Marketplace() {
  const { pedidos } = useOrders()
  const hoje = new Date()
  const [parceiroAba, setParceiroAba] = useState(PARCEIROS[0].id)
  const [subAba,      setSubAba]      = useState('pedidos') // 'pedidos' | 'remessas'
  const [mes,         setMes]         = useState(hoje.getMonth() + 1)
  const [ano,         setAno]         = useState(hoje.getFullYear())
  const [pagamentos,  setPagamentos]  = useState(() => getMarketplacePagamentos())
  const [remessas,    setRemessas]    = useState(() => getMarketplaceRemessas())
  const [selecionados,setSelecionados] = useState(() => new Set())  // Set<pedidoId>
  const [confirmando, setConfirmando] = useState(false)
  const [confirmandoDelete, setConfirmandoDelete] = useState(null) // remessa
  const [obsRemessa,  setObsRemessa]  = useState('')

  // Re-sync do Supabase
  useEffect(() => {
    function onResync() {
      setPagamentos(getMarketplacePagamentos())
      setRemessas(getMarketplaceRemessas())
    }
    window.addEventListener('mini:resynced', onResync)
    return () => window.removeEventListener('mini:resynced', onResync)
  }, [])

  // Limpa seleção ao trocar de parceiro/mês
  useEffect(() => { setSelecionados(new Set()) }, [parceiroAba, mes, ano, subAba])

  function toggleSel(pedidoId) {
    setSelecionados(prev => {
      const s = new Set(prev)
      if (s.has(pedidoId)) s.delete(pedidoId); else s.add(pedidoId)
      return s
    })
  }

  // Enriquece pedidos com parceiro + financeiro + status
  const pedidosEnriquecidos = useMemo(() => {
    return pedidos
      .filter(p => !p.dividido && !p.tipoAjuste)
      .map(p => {
        const parceiro = parceiroDoPedido(p)
        if (!parceiro) return null
        return {
          pedido:    p,
          parceiro,
          fin:       calcularFinanceiro(p),
          statusMkt: statusMarketplace(p, pagamentos),
          pagamento: pagamentos[p.id] || null,
        }
      })
      .filter(Boolean)
  }, [pedidos, pagamentos])

  // Filtra pelo parceiro da aba + mês/ano
  const pedidosDoParceiro = useMemo(() => {
    return pedidosEnriquecidos
      .filter(x => x.parceiro.id === parceiroAba)
      .filter(x => {
        const d = x.pedido.dataVenda || (x.pedido.criadoEm || '').split('T')[0]
        if (!d) return false
        const [y, m] = String(d).split('-').map(Number)
        return y === ano && m === mes
      })
      .sort((a, b) => {
        const ordem = { a_pagar: 0, previsto: 1, pago: 2 }
        if (a.statusMkt !== b.statusMkt) return (ordem[a.statusMkt] || 3) - (ordem[b.statusMkt] || 3)
        return (a.pedido.prazo || '').localeCompare(b.pedido.prazo || '')
      })
  }, [pedidosEnriquecidos, parceiroAba, mes, ano])

  // KPIs
  const totais = useMemo(() => {
    const t = { previsto: 0, aPagar: 0, pago: 0, qtdPrevisto: 0, qtdAPagar: 0, qtdPago: 0 }
    for (const x of pedidosDoParceiro) {
      if (x.statusMkt === 'previsto') { t.previsto += x.fin.parceiro; t.qtdPrevisto++ }
      if (x.statusMkt === 'a_pagar')  { t.aPagar   += x.fin.parceiro; t.qtdAPagar++   }
      if (x.statusMkt === 'pago')     { t.pago     += x.fin.parceiro; t.qtdPago++     }
    }
    return t
  }, [pedidosDoParceiro])

  // Soma dos selecionados
  const selecTotal = useMemo(() => {
    let total = 0, qtd = 0
    for (const x of pedidosDoParceiro) {
      if (selecionados.has(x.pedido.id)) { total += x.fin.parceiro; qtd++ }
    }
    return { total, qtd }
  }, [selecionados, pedidosDoParceiro])

  const parceiroSelecionado = PARCEIROS.find(p => p.id === parceiroAba)

  // ── Ações ──
  function fecharRemessa() {
    if (selecionados.size === 0) return
    const dataFechamento = new Date().toISOString()
    const remessa = {
      id:            uuid(),
      parceiroId:    parceiroAba,
      dataFechamento,
      pedidoIds:     [...selecionados],
      valorTotal:    selecTotal.total,
      observacoes:   obsRemessa.trim(),
    }
    saveMarketplaceRemessa(remessa)
    // Marca cada pedido como pago com referência à remessa
    for (const pedidoId of selecionados) {
      setMarketplacePago(pedidoId, true, dataFechamento.split('T')[0], remessa.id)
    }
    setRemessas(getMarketplaceRemessas())
    setPagamentos(getMarketplacePagamentos())
    setSelecionados(new Set())
    setObsRemessa('')
    setConfirmando(false)
  }

  function desfazerRemessa(r) {
    // Desmarca cada pedido como pago
    for (const pedidoId of r.pedidoIds || []) {
      setMarketplacePago(pedidoId, false)
    }
    deleteMarketplaceRemessa(r.id)
    setRemessas(getMarketplaceRemessas())
    setPagamentos(getMarketplacePagamentos())
    setConfirmandoDelete(null)
  }

  // Gera PDF de UMA remessa específica OU do mês (relatório amplo)
  function gerarPdfRemessa(remessa) {
    const pedidosDaRemessa = remessa.pedidoIds
      .map(id => pedidosEnriquecidos.find(x => x.pedido.id === id))
      .filter(Boolean)
    const parceiro = PARCEIROS.find(p => p.id === remessa.parceiroId)
    _gerarPdfLote({
      titulo:      `Remessa MINI Marcenaria × ${parceiro?.nome}`,
      subtitulo:   `Fechada em ${fmtDataHora(remessa.dataFechamento)}`,
      pedidos:     pedidosDaRemessa,
      observacoes: remessa.observacoes,
      arquivoNome: `Remessa-${parceiro?.nome}-${remessa.dataFechamento.split('T')[0]}.pdf`,
    })
  }

  function gerarPdfMes() {
    _gerarPdfLote({
      titulo:      `Relatório MINI Marcenaria × ${parceiroSelecionado.nome}`,
      subtitulo:   `${MESES[mes-1]} / ${ano}`,
      pedidos:     pedidosDoParceiro,
      observacoes: '',
      arquivoNome: `Marketplace-${parceiroSelecionado.nome}-${ano}-${String(mes).padStart(2,'0')}.pdf`,
    })
  }

  function _gerarPdfLote({ titulo, subtitulo, pedidos, observacoes, arquivoNome }) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
    const pageW = doc.internal.pageSize.getWidth()
    doc.setFillColor(17, 17, 17)
    doc.rect(0, 0, pageW, 20, 'F')
    doc.setFillColor(245, 194, 0)
    doc.rect(0, 18, pageW, 2, 'F')
    doc.setTextColor(245, 194, 0)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(14)
    doc.text(titulo, 12, 12)
    doc.setTextColor(200, 200, 200)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.text(subtitulo, pageW - 12, 12, { align: 'right' })

    const rows = pedidos.map((x, i) => [
      i + 1,
      x.pedido.numero,
      x.pedido.cliente?.nome || '—',
      x.pedido.cliente?.cidade || x.pedido.cliente?.bairro || '—',
      fmtData(x.pedido.prazo),
      fmtMoeda(x.fin.total),
      fmtMoeda(x.fin.parceiro),
    ])

    autoTable(doc, {
      startY: 26,
      head: [['#', 'Pedido', 'Cliente', 'Cidade', 'Prazo', 'Valor total', `Parceiro (${(PARCEIRO_PCT*100).toFixed(0)}%)`]],
      body: rows,
      styles:     { fontSize: 8, cellPadding: 2, textColor: [17,17,17] },
      headStyles: { fillColor: [17,17,17], textColor: [245,194,0], fontSize: 8, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 10,  halign: 'center' },
        1: { cellWidth: 26 },
        2: { cellWidth: 65 },
        3: { cellWidth: 35 },
        4: { cellWidth: 24 },
        5: { cellWidth: 32, halign: 'right' },
        6: { cellWidth: 35, halign: 'right', fontStyle: 'bold' },
      },
      alternateRowStyles: { fillColor: [248,250,252] },
      margin: { left: 10, right: 10 },
    })

    const finalY = doc.lastAutoTable?.finalY || 30
    const somaTotal    = pedidos.reduce((s, x) => s + x.fin.total,    0)
    const somaParceiro = pedidos.reduce((s, x) => s + x.fin.parceiro, 0)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(17, 17, 17)
    doc.text(`Total geral: ${fmtMoeda(somaTotal)}`, 12, finalY + 10)
    doc.setTextColor(204, 136, 0)
    doc.text(`DEVIDO AO PARCEIRO (${(PARCEIRO_PCT*100).toFixed(0)}%): ${fmtMoeda(somaParceiro)}`, 12, finalY + 17)
    if (observacoes) {
      doc.setTextColor(100, 100, 100)
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.text(`Observações: ${observacoes}`, 12, finalY + 25)
    }
    doc.save(arquivoNome)
  }

  // Remessas do parceiro selecionado (histórico)
  const remessasDoParceiro = useMemo(
    () => remessas
      .filter(r => r.parceiroId === parceiroAba)
      .sort((a, b) => (b.dataFechamento || '').localeCompare(a.dataFechamento || '')),
    [remessas, parceiroAba]
  )

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 pb-32">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-slate-800">Marketplace</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          Pedidos fabricados por marcenarias parceiras — {(PARCEIRO_PCT*100).toFixed(0)}% do valor total é do parceiro (móveis + frete)
        </p>
      </div>

      {/* Tabs de parceiro */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-xl p-1 w-fit flex-wrap">
        {PARCEIROS.map(p => {
          const ativa = parceiroAba === p.id
          return (
            <button
              key={p.id}
              onClick={() => setParceiroAba(p.id)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all
                ${ativa ? 'bg-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              style={ativa ? { color: p.cor } : {}}
            >
              {p.nome}
              <span className="text-[10px] font-normal text-slate-400 ml-1">{p.cidade}</span>
            </button>
          )
        })}
      </div>

      {/* Sub-tabs: Pedidos | Remessas */}
      <div className="flex gap-1 mb-5 border-b border-slate-200">
        <button
          onClick={() => setSubAba('pedidos')}
          className={`px-4 py-2 text-sm font-semibold transition-all border-b-2 -mb-px
            ${subAba === 'pedidos' ? 'border-[#F5C200] text-slate-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Pedidos
        </button>
        <button
          onClick={() => setSubAba('remessas')}
          className={`px-4 py-2 text-sm font-semibold transition-all border-b-2 -mb-px
            ${subAba === 'remessas' ? 'border-[#F5C200] text-slate-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Remessas
          {remessasDoParceiro.length > 0 && (
            <span className="ml-1.5 bg-slate-100 text-slate-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
              {remessasDoParceiro.length}
            </span>
          )}
        </button>
      </div>

      {subAba === 'pedidos' && (
        <>
          {/* Mês/Ano seletor + PDF do mês */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="flex gap-1 bg-slate-100 rounded-xl p-1 overflow-x-auto">
              {MESES.map((nome, i) => {
                const num = i + 1
                const ativo = mes === num
                return (
                  <button key={num} onClick={() => setMes(num)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap
                      ${ativo ? 'bg-white text-[#111111] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  >
                    {nome.slice(0, 3)}
                  </button>
                )
              })}
            </div>
            <select value={ano} onChange={e => setAno(Number(e.target.value))} className="input text-sm w-24">
              {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              onClick={gerarPdfMes}
              disabled={pedidosDoParceiro.length === 0}
              className="btn-secondary text-sm ml-auto disabled:opacity-40 disabled:cursor-not-allowed"
              title="Gera PDF de TODOS os pedidos do mês (não é uma remessa)"
            >
              <FileDown size={14} />
              PDF do mês
            </button>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="card p-4 border-l-4 border-l-slate-400">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Previsto</p>
                  <p className="text-2xl font-black text-slate-700 mt-1 tabular-nums">{fmtMoeda(totais.previsto)}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{totais.qtdPrevisto} em produção</p>
                </div>
                <Clock size={20} className="text-slate-300" />
              </div>
            </div>
            <div className="card p-4 border-l-4 border-l-amber-400 bg-amber-50/30">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-amber-700 font-bold">A pagar</p>
                  <p className="text-2xl font-black text-amber-700 mt-1 tabular-nums">{fmtMoeda(totais.aPagar)}</p>
                  <p className="text-xs text-amber-600 mt-0.5">{totais.qtdAPagar} entregue{totais.qtdAPagar !== 1 ? 's' : ''}</p>
                </div>
                <TrendingUp size={20} className="text-amber-400" />
              </div>
            </div>
            <div className="card p-4 border-l-4 border-l-emerald-400 bg-emerald-50/30">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Já pago</p>
                  <p className="text-2xl font-black text-emerald-700 mt-1 tabular-nums">{fmtMoeda(totais.pago)}</p>
                  <p className="text-xs text-emerald-600 mt-0.5">{totais.qtdPago} quitado{totais.qtdPago !== 1 ? 's' : ''}</p>
                </div>
                <CheckCircle2 size={20} className="text-emerald-400" />
              </div>
            </div>
          </div>

          {/* Tabela */}
          {pedidosDoParceiro.length === 0 ? (
            <div className="card p-12 text-center">
              <Package className="mx-auto text-slate-200 mb-3" size={40} />
              <p className="text-sm text-slate-400">
                Nenhum pedido de <strong>{parceiroSelecionado?.nome}</strong> em {MESES[mes-1]}/{ano}
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="w-10 px-3 py-2.5"></th>
                    <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Pedido</th>
                    <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Cliente</th>
                    <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Cidade</th>
                    <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Prazo</th>
                    <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                    <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Parceiro</th>
                    <th className="px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {pedidosDoParceiro.map(x => {
                    const p = x.pedido
                    const podeSelecionar = x.statusMkt === 'a_pagar'
                    const marcado = selecionados.has(p.id)
                    return (
                      <tr
                        key={p.id}
                        onClick={() => podeSelecionar && toggleSel(p.id)}
                        className={`
                          ${podeSelecionar ? 'cursor-pointer' : ''}
                          ${x.statusMkt === 'pago' ? 'bg-emerald-50/30' : ''}
                          ${marcado ? 'bg-[#F5C200]/10' : (x.statusMkt === 'a_pagar' ? 'bg-amber-50/30 hover:bg-amber-50/60' : '')}
                        `}
                      >
                        <td className="px-3 py-3 text-center">
                          {podeSelecionar ? (
                            <input
                              type="checkbox"
                              checked={marcado}
                              onChange={() => toggleSel(p.id)}
                              onClick={e => e.stopPropagation()}
                              className="w-4 h-4 accent-[#F5C200] cursor-pointer"
                            />
                          ) : (
                            <span className="w-4 h-4 inline-block" />
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <p className="text-xs font-mono font-bold text-slate-700">{p.numero}</p>
                        </td>
                        <td className="px-3 py-3">
                          <p className="font-medium text-slate-800 text-xs">{p.cliente?.nome || '—'}</p>
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-500">
                          {p.cliente?.cidade || p.cliente?.bairro || '—'}
                        </td>
                        <td className="px-3 py-3 text-xs text-slate-500">{fmtData(p.prazo)}</td>
                        <td className="px-3 py-3 text-right">
                          <p className="text-xs font-semibold text-slate-700 tabular-nums">{fmtMoeda(x.fin.total)}</p>
                          {x.fin.frete > 0 && (
                            <p className="text-[9px] text-slate-400 tabular-nums">
                              +{fmtMoeda(x.fin.frete)} frete
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <p className="text-xs font-bold text-[#CC8800] tabular-nums">{fmtMoeda(x.fin.parceiro)}</p>
                        </td>
                        <td className="px-3 py-3 text-center">
                          {x.statusMkt === 'previsto' && (
                            <span className="text-[9px] px-2 py-1 rounded-full bg-slate-100 text-slate-500 font-bold uppercase tracking-wider">
                              Previsto
                            </span>
                          )}
                          {x.statusMkt === 'a_pagar' && (
                            <span className="text-[9px] px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-bold uppercase tracking-wider">
                              A pagar
                            </span>
                          )}
                          {x.statusMkt === 'pago' && (
                            <span className="text-[9px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider" title={x.pagamento?.dataPagamento && `Pago em ${fmtData(x.pagamento.dataPagamento)}`}>
                              ✓ Pago
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Sub-aba Remessas */}
      {subAba === 'remessas' && (
        <div>
          {remessasDoParceiro.length === 0 ? (
            <div className="card p-12 text-center">
              <Send className="mx-auto text-slate-200 mb-3" size={40} />
              <p className="text-sm text-slate-400">
                Nenhuma remessa fechada com <strong>{parceiroSelecionado?.nome}</strong> ainda.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Vá em <strong>Pedidos</strong>, selecione pedidos entregues e clique em <strong>Fechar remessa</strong>.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {remessasDoParceiro.map(r => (
                <div key={r.id} className="card p-4 flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-slate-400">#{r.id.slice(0, 8)}</p>
                    <p className="text-sm font-bold text-slate-800 mt-0.5">
                      Fechada em {fmtDataHora(r.dataFechamento)}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {r.pedidoIds.length} pedido{r.pedidoIds.length !== 1 ? 's' : ''} · Valor pago:
                      {' '}<strong className="text-[#CC8800]">{fmtMoeda(r.valorTotal)}</strong>
                    </p>
                    {r.observacoes && (
                      <p className="text-xs text-slate-400 italic mt-1">"{r.observacoes}"</p>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => gerarPdfRemessa(r)}
                      className="btn-secondary text-xs"
                    >
                      <FileDown size={12} />
                      PDF
                    </button>
                    <button
                      onClick={() => setConfirmandoDelete(r)}
                      className="btn-secondary text-xs text-red-500 hover:bg-red-50"
                      title="Desfazer remessa (pedidos voltam pra 'A pagar')"
                    >
                      <Trash2 size={12} />
                      Desfazer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Barra flutuante de seleção (só na aba Pedidos, quando há seleção) */}
      {subAba === 'pedidos' && selecionados.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-[#111111] text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4 max-w-[calc(100vw-2rem)]">
          <div>
            <p className="text-xs text-white/60 uppercase tracking-widest font-bold">Selecionados</p>
            <p className="text-lg font-black tabular-nums">
              {selecTotal.qtd} pedido{selecTotal.qtd !== 1 ? 's' : ''} · {fmtMoeda(selecTotal.total)}
            </p>
          </div>
          <button
            onClick={() => setSelecionados(new Set())}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            title="Limpar seleção"
          >
            <X size={16} />
          </button>
          <button
            onClick={() => setConfirmando(true)}
            className="bg-[#F5C200] hover:bg-[#e6b800] text-[#111111] font-bold text-sm px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
          >
            <Send size={14} />
            Fechar remessa
          </button>
        </div>
      )}

      {/* Modal de confirmação de fechamento */}
      {confirmando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmando(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-slate-800 text-lg mb-1">Fechar remessa?</h3>
            <p className="text-sm text-slate-500 mb-4">
              Vai criar uma remessa com <strong>{selecTotal.qtd} pedido{selecTotal.qtd !== 1 ? 's' : ''}</strong> de <strong>{parceiroSelecionado?.nome}</strong>, totalizando <strong className="text-[#CC8800]">{fmtMoeda(selecTotal.total)}</strong>. Esses pedidos ficam marcados como pagos.
            </p>
            <label className="label">Observação (opcional)</label>
            <textarea
              value={obsRemessa}
              onChange={e => setObsRemessa(e.target.value)}
              rows={2}
              className="input resize-none mb-4"
              placeholder="Ex: PIX pra Piemonte em 30/06/2026"
            />
            <div className="flex gap-3">
              <button onClick={() => setConfirmando(false)} className="btn-secondary flex-1 justify-center">Cancelar</button>
              <button onClick={fecharRemessa} className="btn-primary flex-1 justify-center">
                <Send size={14} />
                Fechar remessa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmação de desfazer remessa */}
      {confirmandoDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmandoDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-slate-800 text-lg mb-1">Desfazer remessa?</h3>
            <p className="text-sm text-slate-500 mb-4">
              Os <strong>{confirmandoDelete.pedidoIds.length} pedido{confirmandoDelete.pedidoIds.length !== 1 ? 's' : ''}</strong> voltam pra "A pagar" e a remessa é excluída. Não some com os dados dos pedidos.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmandoDelete(null)} className="btn-secondary flex-1 justify-center">Cancelar</button>
              <button onClick={() => desfazerRemessa(confirmandoDelete)} className="btn-danger flex-1 justify-center">
                Desfazer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
